/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import { Tooltip, Row, Col, Modal, Form, Button, Input, message } from 'antd';
import { isEmpty } from 'lodash';
import { Storage } from 'aws-amplify';
import PaginationTable from '../../../shared/PaginationTable';

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
  TooltipText,
} from "../../../shared/globalStyling/styledText";
import { EditOutlined, LoadingOutlined, SaveOutlined } from "@ant-design/icons";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { performCreativeSearch } from "../../Creatives/utils/mediaUtils";

// Move VideoModal outside to prevent recreation on every render
const VideoModal = ({ videoUrl, visible, onClose, videoName }) => {
  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="80vw"
      centered
      closable={false}
      mask={true}
      maskStyle={{
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        backdropFilter: "blur(3px)"
      }}
      maskClosable={false}
      style={{
        maxWidth: "900px",
        background: "transparent",
        boxShadow: "none"
      }}
      bodyStyle={{
        padding: "60px 0 40px 0",
        height: "70vh",
        background: "transparent",
        display: "flex",
        flexDirection: "column"
      }}
      className="creative-preview-modal"
      destroyOnClose={true}
    >
      {/* Video Name */}
      <div style={{
        position: "absolute",
        top: "10px",
        left: "50px",
        zIndex: 10,
        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
        color: "white",
        padding: "6px 16px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        letterSpacing: "0.5px",
        maxWidth: "300px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}>
        {videoName || 'Video Unit'}
      </div>

      {/* Close Button */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          top: "10px",
          right: "50px",
          zIndex: 10,
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
          color: "white",
          width: "32px",
          height: "32px",
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "600",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          transition: "all 0.2s ease",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15))";
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </div>

      {/* Video Player */}
      <div style={{
        height: "calc(70vh - 100px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 60px"
      }}>
        {videoUrl && (
          <video
            controls
            width="100%"
            height="100%"
            key={videoUrl}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              borderRadius: "8px",
              boxShadow: "0 15px 40px rgba(0, 0, 0, 0.4)",
              objectFit: "contain"
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    </Modal>
  );
};

const VideoTable = ({
  orgMedias,
  videoCreativeIds,
  setVideoCreativeKeys,
  setVideoCreativeIds,
  updateOrgMedia,
  searchText,
  currentPage: propCurrentPage,
  onPageChange,
  clearSelectionTrigger,
}) => {
  const history = useHistory();
  const location = useLocation(); // Keep for now to prevent runtime error
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const [medias, setMedias] = useState([]);
  const [localSelectedKeys, setLocalSelectedKeys] = useState([]);
  const [editMedia, setEditMedia] = useState(false);
  const [editMediaSubmit, setEditMediaSubmit] = useState(false);
  const [creativeID, setCreativeID] = useState('');
  const [name, setName] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [url, setUrl] = useState('');
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const [videoDurations, setVideoDurations] = useState({});

  // Stable handleRowClick function to prevent useEffect loops
  const handleRowClick = useCallback((videoUrl, videoName) => {
    setSelectedVideo(videoUrl);
    setSelectedVideoName(videoName);
    setModalVisible(true);
  }, []);

  // Stable handleModalClose function
  const handleModalClose = useCallback(() => {
    setModalVisible(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    // The purpose of this is it renews the token of the video urls
    const processImages = async (orgMedias) => {
      const medias = await Promise.all(
        orgMedias.map(async (media) => {
          const response = await Storage.get(media.key, { expires: 600000 });
          media.image = (
            <VideoPreviewCell videoUrl={response} onClick={() => handleRowClick(response, media.name)} />
          );
          return media;
        })
      );
      if (isMounted) {
        setMedias(medias);
      }
    };

    const mediaData = orgMedias ? orgMedias.medias : [];

    // Use standardized search function for video media items
    const searchFiltered = performCreativeSearch(mediaData, searchText, 'media');

    if (!isEmpty(searchFiltered)) {
      processImages(searchFiltered);
    } else if (isMounted) {
      setMedias(searchFiltered);
    }

    return () => {
      isMounted = false;
    };
  }, [orgMedias, searchText, handleRowClick]);

  // Clear local selection when parent clears selection (after assign/create operations)
  useEffect(() => {
    if (Array.isArray(videoCreativeIds) && videoCreativeIds.length === 0) {
      setLocalSelectedKeys([]);
    }
  }, [videoCreativeIds]);

  // Clear local selection when clearSelectionTrigger changes
  useEffect(() => {
    if (clearSelectionTrigger > 0) {
      setLocalSelectedKeys([]);
    }
  }, [clearSelectionTrigger]);

  // Helper function to detect video duration with mount check
  const detectVideoDuration = async (mediaKey, isMountedRef) => {
    if (videoDurations[mediaKey]) {
      return videoDurations[mediaKey];
    }

    try {
      const videoUrl = await Storage.get(mediaKey, { expires: 600000 });

      if (!isMountedRef.current) return null; // Exit if component unmounted

      const duration = await new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        const cleanup = () => {
          video.onloadedmetadata = null;
          video.onerror = null;
          video.src = '';
          video.load(); // Clear video resources
        };

        video.onloadedmetadata = () => {
          const duration = Math.round(video.duration);
          cleanup();
          resolve(duration);
        };

        video.onerror = () => {
          cleanup();
          resolve(null);
        };

        video.src = videoUrl;
      });

      if (duration && isMountedRef.current) {
        // Only update state if component is still mounted
        setVideoDurations(prev => ({ ...prev, [mediaKey]: duration }));
        return duration;
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.warn('Failed to detect duration for:', mediaKey, error);
      }
    }

    return null;
  };

  // Helper function to format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Effect to detect video durations when medias load
  useEffect(() => {
    const isMountedRef = { current: true };

    const detectDurationsForVideos = async () => {
      if (!medias || medias.length === 0) return;

      // Get video keys that we don't have duration for yet
      const videoKeysToDetect = medias
        .filter(media => media.key && !videoDurations[media.key])
        .map(media => media.key)
        .slice(0, 10); // Process max 10 at a time

      // Detect durations with mount check
      for (const mediaKey of videoKeysToDetect) {
        if (isMountedRef.current) {
          await detectVideoDuration(mediaKey, isMountedRef);
        }
      }
    };

    detectDurationsForVideos();

    return () => {
      isMountedRef.current = false;
    };
  }, [medias, videoDurations]);

  // Sync currentPage with prop
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  const extraColumn = {
    title: "",
    dataIndex: "image",
    key: "image",
    width: "8%",
    align: "left",
  };

  let columns = [
    {
      title: <TableColumnTitle text={'Video Ad Unit Name'} />,
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      align: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => {
        if (text) {
          return (
            <Row
              type="flex"
              justify="start"
              align="middle"
              onClick={() => {
                // Clean URL navigation (consistent with ad groups)
                history.push(`/creatives/video/units/${record.id}`);
              }}
            >
              <Col span={24}>
                {text.length > 45 ? (
                  <Tooltip title={<TooltipText text={text} />}>
                    <span>
                      <PrimaryTableRowText text={text.slice(0, 46).concat('...')} />
                    </span>
                  </Tooltip>
                ) : (
                  <PrimaryTableRowText text={text} />
                )}
              </Col>
            </Row>
          );
        } else {
          return <div />;
        }
      },
    },
    {
      title: <TableColumnTitle text={"Aspect"} />,
      dataIndex: "aspect",
      key: "aspect",
      width: "10%",
      align: "left",
      sorter: (a, b) => a.width - b.width || a.height - b.height,
      render: (_, record) => (
        <div>
          <PrimaryTableRowText text={`${record.width}x${record.height}`} />
        </div>
      ),
    },
    {
      title: <TableColumnTitle text={'File Type'} />,
      dataIndex: 'fileType',
      key: 'fileType',
      width: '12%',
      align: 'left',
      sorter: (a, b) => a.fileType.localeCompare(b.fileType),
      render: (fileType) => <PrimaryTableRowText text={fileType} />,
    },
    {
      title: <TableColumnTitle text={'Duration'} />,
      dataIndex: 'key',
      key: 'duration',
      width: '10%',
      align: 'left',
      sorter: (a, b) => {
        const durationA = videoDurations[a.key] || 0;
        const durationB = videoDurations[b.key] || 0;
        return durationA - durationB;
      },
      render: (mediaKey) => {
        const duration = videoDurations[mediaKey];
        return <PrimaryTableRowText text={formatDuration(duration)} />;
      },
    },
    {
      title: <TableColumnTitle text={'Date Uploaded'} />,
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '18%',
      align: 'left',
      sorter: (a, b) => {
        const dateA = moment(a.createdAt);
        const dateB = moment(b.createdAt);
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      },
      render: (createdAt) => {
        return <PrimaryTableRowText text={moment(createdAt).format('MM/DD/YYYY hh:mm A')} />;
      },
    },
    {
      title: '',
      key: 'edit',
      width: '10%',
      render: (creatives) => (
        <EditOutlined
          style={{ fontSize: '1.25rem' }}
          onClick={() => {
            editOrgMedia(creatives);
          }}
        />
      ),
    },
  ];

  if (!updateOrgMedia) {
    // Filter edit column if updateOrgMedia is not passed
    columns = columns.filter((column) => column.key !== 'edit');
  }

  const editOrgMedia = async (data) => {
    form.resetFields();

    setEditMedia(true);
    setCreativeID(data.id);
    setName(data.name);
    setCloneName(data.name);
    setUrl(data.url);
    form.setFieldsValue({
      name: data.name,
    });
  };

  if (orgMedias && !isEmpty(orgMedias)) {
    columns.unshift(extraColumn);
  }

  const handleSubmit = async () => {
    if (name === cloneName) return;

    try {
      setEditMediaSubmit(true);

      message.loading('Action in progress...', 0);

      const CapitalizeCreativeName = name.charAt(0).toUpperCase() + name.slice(1);

      await updateOrgMedia({
        variables: {
          creativeID,
          newUrl: url,
          newName: CapitalizeCreativeName,
        },
      });

      form.resetFields();
      setEditMediaSubmit(false);
      setCreativeID('');
      setUrl('');
      setName('');
      setEditMedia(false);
      message.destroy();
      message.success(`Successfully updated the creative name`);
    } catch (err) {
      message.destroy();
      message.error(`Error updating creative name`);
    }
  };

  const rowSelection = {
    selectedRowKeys: localSelectedKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      const creativeKeys = selectedRows.map(row => {
        return row.key;
      });

      setLocalSelectedKeys(selectedRowKeys);
      setVideoCreativeIds(selectedRowKeys);
      setVideoCreativeKeys(creativeKeys);
    },
    /* UNUSED: Table selection options - removed from UI design (consistent with CreativeTable)
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
    */
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [selectedVideoName, setSelectedVideoName] = useState('');


  const VideoPreviewCell = ({ videoUrl, onClick }) => {
    return (
      <div
        onClick={onClick}
        style={{
          width: '60px', // Smaller thumbnail size
          height: '45px', // Smaller thumbnail size
          cursor: 'pointer',
        }}
      >
        <video
          src={videoUrl}
          type="video/mp4"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          muted
          autoPlay
          loop
        />
      </div>
    );
  };


  return (
    <>
      <VideoModal
        key={selectedVideo}
        videoUrl={selectedVideo}
        videoName={selectedVideoName}
        visible={modalVisible}
        onClose={handleModalClose}
      />
      <Modal
        title="Edit Media"
        open={editMedia}
        centered
        footer={null}
        onCancel={() => {
          setEditMedia(false);
          form.resetFields();
        }}
      >
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
          initialValues={{
            name,
          }}
        >
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input
                  placeholder="Name of Creative"
                  onChange={(e) => {
                    const CapitalizeCreativeName =
                      e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
                    setName(CapitalizeCreativeName);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  disabled={name === cloneName}
                  icon={editMediaSubmit ? <LoadingOutlined /> : <SaveOutlined />}
                  htmlType="submit"
                  type="primary"
                >
                  Update
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <PaginationTable
        /* UNUSED: footer calculation - removed from table design */
        // footer={medias && medias.slice(currentPage * 10 - 10, currentPage * 10)}
        bordered={false}
        columns={columns}
        data={medias}
        scroll={{ x: 800, y: false }}
        paginationSize={'default'}
        size={'default'}
        total={medias && medias.length}
        current={currentPage}
        pageSize={pageSize}
        rowKey="id"
        rowSelection={rowSelection}
        onChange={paginationObject => {
          setPageSize(paginationObject.pageSize);
          setCurrentPage(paginationObject.current);
          if (onPageChange) {
            onPageChange(paginationObject.current);
          }
        }}
      />
    </>
  );
};

export default VideoTable;
